import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import { client } from '@/lib/api';
import { formatCents } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SummaryBar from './SummaryBar';

type FilterTab = 'all' | 'upcoming' | 'paid' | 'overdue';

interface PaymentsTabProps {
  projectId: string;
  onSelectItem?: (itemId: string) => void;
}

export default function PaymentsTab({ projectId, onSelectItem }: PaymentsTabProps) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<FilterTab>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['payment-schedule', projectId],
    queryFn: async () => {
      const res = await client.api.projects[':id']['payment-schedule'].$get({
        param: { id: projectId },
      });
      if (!res.ok) throw new Error('Failed to fetch payment schedule');
      return res.json();
    },
    enabled: !!projectId,
  });

  const togglePaidMutation = useMutation({
    mutationFn: async (paymentId: string) => {
      const res = await fetch(`/api/payments/${paymentId}/toggle-paid`, {
        method: 'PATCH',
      });
      if (!res.ok) throw new Error('Failed to toggle');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-schedule', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const summary = data?.summary;
  const payments = data?.payments ?? [];

  const filtered = payments.filter((m) => {
    if (filter === 'upcoming') return !m.paidAt && !m.isOverdue;
    if (filter === 'paid') return !!m.paidAt;
    if (filter === 'overdue') return m.isOverdue;
    return true;
  });

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: payments.length },
    { key: 'upcoming', label: 'Upcoming', count: payments.filter((m) => !m.paidAt && !m.isOverdue).length },
    { key: 'paid', label: 'Paid', count: payments.filter((m) => !!m.paidAt).length },
    { key: 'overdue', label: 'Overdue', count: payments.filter((m) => m.isOverdue).length },
  ];

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading payment schedule...</p>;
  }
  if (!summary) {
    return <p className="text-sm text-muted-foreground">No data available.</p>;
  }

  return (
    <div className="space-y-4">
      <SummaryBar>
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
          <div className="space-y-1">
            <p className="label-caps text-xs text-muted-foreground">Committed</p>
            <p className="text-lg font-semibold tabular-nums">{formatCents(summary.totalCommitted)}</p>
          </div>
          <div className="space-y-1">
            <p className="label-caps text-xs text-muted-foreground">Paid</p>
            <p className="text-lg font-semibold tabular-nums text-primary">{formatCents(summary.totalPaid)}</p>
          </div>
          <div className="space-y-1">
            <p className="label-caps text-xs text-muted-foreground">Remaining</p>
            <p className="text-lg font-semibold tabular-nums">{formatCents(summary.totalRemaining)}</p>
          </div>
          <div className="space-y-1">
            <p className="label-caps text-xs text-muted-foreground">Overdue</p>
            <p className={`text-lg font-semibold tabular-nums ${summary.totalOverdue > 0 ? 'text-destructive' : ''}`}>
              {formatCents(summary.totalOverdue)}
            </p>
          </div>
        </div>

        {summary.totalCommitted > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {formatCents(summary.totalPaid)} of {formatCents(summary.totalCommitted)} paid
              </span>
              <span className="text-muted-foreground">
                {Math.round((summary.totalPaid / summary.totalCommitted) * 100)}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${Math.round((summary.totalPaid / summary.totalCommitted) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </SummaryBar>

      <div className="flex gap-1 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
              filter === tab.key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground italic">
          No payments match this filter.
        </p>
      ) : (
        <Card className="rounded-2xl shadow-ambient overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="px-4 py-3 text-left label-caps text-xs font-medium text-muted-foreground w-8" />
                    <th className="px-4 py-3 text-left label-caps text-xs font-medium text-muted-foreground">Description</th>
                    <th className="px-4 py-3 text-left label-caps text-xs font-medium text-muted-foreground">Vendor</th>
                    <th className="px-4 py-3 text-left label-caps text-xs font-medium text-muted-foreground">Due Date</th>
                    <th className="px-4 py-3 text-right label-caps text-xs font-medium text-muted-foreground">Amount</th>
                    <th className="px-4 py-3 text-right label-caps text-xs font-medium text-muted-foreground w-28" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m) => {
                    const isPaid = !!m.paidAt;
                    return (
                      <tr
                        key={m.id}
                        onClick={() => onSelectItem?.(m.itemId)}
                        className={`border-b last:border-0 hover:bg-muted/20 transition-colors ${onSelectItem ? 'cursor-pointer' : ''}`}
                      >
                        <td className="px-4 py-3">
                          {isPaid ? (
                            <CheckCircle2 className="size-4 text-primary" />
                          ) : m.isOverdue ? (
                            <AlertCircle className="size-4 text-destructive" />
                          ) : (
                            <Circle className="size-4 text-amber-500" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={isPaid ? 'text-muted-foreground' : 'font-medium'}>
                            {m.description || 'Payment'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{m.vendorName}</td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                          {m.dueDate
                            ? new Date(m.dueDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                timeZone: 'UTC',
                              })
                            : m.dueDateDescription || 'No date'}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium whitespace-nowrap">
                          {formatCents(m.amount)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isPaid ? (
                            <span className="text-xs text-primary">
                              Paid{m.paidAt ? ` ${new Date(m.paidAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                            </span>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePaidMutation.mutate(m.id);
                              }}
                              disabled={togglePaidMutation.isPending}
                            >
                              Mark Paid
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
